// ============================================================
//  RuleDetailDrawer — v31
//  Side-Drawer (Sheet von rechts) fuer Create/Edit/Delete einer
//  Promotion-Rule. Strukturierter Conditions-Editor.
// ============================================================

import { useEffect, useState } from 'react'
import { X, Trash2 } from 'lucide-react'
import { ApiError } from '@/api/client'
import {
  useCreateRule,
  useDeleteRule,
  useUpdateRule,
  fetchRuleEventsCount,
  type PromotionRule,
  type RuleConditions,
  type RuleStage,
} from '@/api/promotions'
import { cn } from '@/lib/utils'

const STAGES: { value: RuleStage; label: string }[] = [
  { value: 'auto', label: 'auto' },
  { value: 'broad', label: 'broad' },
  { value: 'phrase', label: 'phrase' },
  { value: 'exact', label: 'exact' },
  { value: 'negative', label: 'negative' },
  { value: 'bid_increase', label: 'bid_increase' },
  { value: 'bid_decrease', label: 'bid_decrease' },
  { value: 'any', label: 'any' },
]

interface FormState {
  name: string
  description: string
  fromStage: RuleStage
  toStage: RuleStage
  priority: number
  isActive: boolean
  autoApply: boolean
  maxPerDay: number
  cooldownMinutes: number
  // Conditions
  minClicks: number | ''
  minOrders: number | ''
  maxAcos: number | ''
  minAcos: number | ''
  minSpend: number | ''
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  fromStage: 'auto',
  toStage: 'broad',
  priority: 50,
  isActive: true,
  autoApply: false,
  maxPerDay: 20,
  cooldownMinutes: 1440,
  minClicks: '',
  minOrders: '',
  maxAcos: '',
  minAcos: '',
  minSpend: '',
}

function ruleToForm(rule: PromotionRule): FormState {
  const c = (rule.conditions ?? {}) as RuleConditions
  const num = (v: unknown): number | '' =>
    typeof v === 'number' ? v : v === undefined || v === null ? '' : Number(v) || ''
  return {
    name: rule.name,
    description: rule.description ?? '',
    fromStage: rule.fromStage as RuleStage,
    toStage: rule.toStage as RuleStage,
    priority: rule.priority,
    isActive: rule.isActive === 1,
    autoApply: rule.autoApply === 1,
    maxPerDay: rule.maxPerDay,
    cooldownMinutes: rule.cooldownMinutes,
    minClicks: num(c.minClicks),
    minOrders: num(c.minOrders),
    maxAcos: num(c.maxAcos),
    minAcos: num(c.minAcos),
    minSpend: num(c.minSpend),
  }
}

function buildConditions(form: FormState): RuleConditions {
  const out: RuleConditions = {}
  if (form.minClicks !== '') out.minClicks = Number(form.minClicks)
  if (form.minOrders !== '') out.minOrders = Number(form.minOrders)
  if (form.maxAcos !== '') out.maxAcos = Number(form.maxAcos)
  if (form.minAcos !== '') out.minAcos = Number(form.minAcos)
  if (form.minSpend !== '') out.minSpend = Number(form.minSpend)
  return out
}

interface RuleDetailDrawerProps {
  open: boolean
  onClose: () => void
  rule?: PromotionRule | null
}

type Mode = 'create' | 'edit'

export default function RuleDetailDrawer({ open, onClose, rule }: RuleDetailDrawerProps) {
  const mode: Mode = rule ? 'edit' : 'create'
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [eventsCount, setEventsCount] = useState<number | null>(null)

  const create = useCreateRule()
  const update = useUpdateRule()
  const del = useDeleteRule()

  // Form zuruecksetzen wenn Drawer oeffnet oder rule wechselt
  useEffect(() => {
    if (!open) return
    setSubmitError(null)
    setEventsCount(null)
    if (rule) {
      setForm(ruleToForm(rule))
      // Pre-fetch affectedEvents fuer Delete-Confirm
      fetchRuleEventsCount(rule.id)
        .then((n) => setEventsCount(n))
        .catch(() => setEventsCount(null))
    } else {
      setForm(EMPTY_FORM)
    }
  }, [open, rule])

  // ESC schliesst
  useEffect(() => {
    if (!open) return
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function numberOrEmpty(raw: string): number | '' {
    if (raw === '') return ''
    const n = Number(raw)
    return Number.isFinite(n) ? n : ''
  }

  async function handleSubmit() {
    setSubmitError(null)
    if (!form.name.trim()) {
      setSubmitError('Name ist erforderlich')
      return
    }
    if (form.priority < 1) {
      setSubmitError('Priority muss >= 1 sein')
      return
    }
    if (form.cooldownMinutes < 60) {
      setSubmitError('Cooldown muss >= 60 Minuten sein')
      return
    }
    const conditions = buildConditions(form)
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          name: form.name.trim(),
          description: form.description.trim() || null,
          fromStage: form.fromStage,
          toStage: form.toStage,
          conditions,
          priority: form.priority,
          isActive: form.isActive ? 1 : 0,
          autoApply: form.autoApply ? 1 : 0,
          maxPerDay: form.maxPerDay,
          cooldownMinutes: form.cooldownMinutes,
        })
      } else if (rule) {
        await update.mutateAsync({
          id: rule.id,
          name: form.name.trim(),
          description: form.description.trim() || null,
          fromStage: form.fromStage,
          toStage: form.toStage,
          priority: form.priority,
          isActive: form.isActive ? 1 : 0,
          autoApply: form.autoApply ? 1 : 0,
          maxPerDay: form.maxPerDay,
          cooldownMinutes: form.cooldownMinutes,
          conditions,
        })
      }
      onClose()
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message)
      } else if (err instanceof Error) {
        setSubmitError(err.message)
      } else {
        setSubmitError('Unbekannter Fehler')
      }
    }
  }

  async function handleDelete() {
    if (!rule) return
    const count = eventsCount ?? 0
    const msg =
      count > 0
        ? `Diese Regel hat ${count} verknuepfte Events. Beim Loeschen werden die Events behalten, aber die Rule-Verknuepfung entfernt (ruleId=null). Wirklich loeschen?`
        : `Regel '${rule.name}' wirklich loeschen?`
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return
    setSubmitError(null)
    try {
      await del.mutateAsync(rule.id)
      onClose()
    } catch (err) {
      if (err instanceof ApiError || err instanceof Error) {
        setSubmitError(err.message)
      } else {
        setSubmitError('Unbekannter Fehler')
      }
    }
  }

  const isPending = create.isPending || update.isPending || del.isPending

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'create' ? 'Neue Regel anlegen' : `Regel ${rule?.name} bearbeiten`}
    >
      <div className="flex h-full w-full max-w-[520px] flex-col bg-[var(--background)] shadow-2xl">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-[var(--border)] px-5 py-4">
          <div>
            <h2 className="text-[15px] font-bold text-[var(--foreground)]">
              {mode === 'create' ? 'Neue Regel anlegen' : 'Regel bearbeiten'}
            </h2>
            {mode === 'edit' && rule && (
              <p className="text-[11.5px] text-[var(--muted-foreground)]">ID #{rule.id}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
            aria-label="Schliessen"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Body — scrollable form */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Sektion 1: Identitaet */}
          <Section title="Identitaet">
            <Field label="Name *">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className={inputCls}
                placeholder="z.B. AUTO_BROAD_PROMO"
                maxLength={100}
              />
            </Field>
            <Field label="Beschreibung">
              <textarea
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                className={cn(inputCls, 'min-h-[64px] resize-y')}
                placeholder="Was diese Regel macht (optional)"
                maxLength={500}
              />
            </Field>
            <Field label="Priority (niedriger = wichtiger)">
              <input
                type="number"
                min={1}
                max={999}
                value={form.priority}
                onChange={(e) => setField('priority', Number(e.target.value) || 1)}
                className={inputCls}
              />
            </Field>
          </Section>

          {/* Sektion 2: Flow */}
          <Section title="Flow">
            <div className="grid grid-cols-2 gap-3">
              <Field label="From Stage">
                <select
                  value={form.fromStage}
                  onChange={(e) => setField('fromStage', e.target.value as RuleStage)}
                  className={inputCls}
                >
                  {STAGES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="To Stage">
                <select
                  value={form.toStage}
                  onChange={(e) => setField('toStage', e.target.value as RuleStage)}
                  className={inputCls}
                >
                  {STAGES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>

          {/* Sektion 3: Bedingungen */}
          <Section
            title="Bedingungen (alle leer = trifft immer)"
            hint="Ein Search-Term-Eintrag muss alle gesetzten Bedingungen erfuellen, damit die Regel feuert."
          >
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min Clicks">
                <input
                  type="number"
                  min={0}
                  value={form.minClicks}
                  onChange={(e) => setField('minClicks', numberOrEmpty(e.target.value))}
                  className={inputCls}
                />
              </Field>
              <Field label="Min Orders">
                <input
                  type="number"
                  min={0}
                  value={form.minOrders}
                  onChange={(e) => setField('minOrders', numberOrEmpty(e.target.value))}
                  className={inputCls}
                />
              </Field>
              <Field label="Max ACoS (%)">
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={form.maxAcos}
                  onChange={(e) => setField('maxAcos', numberOrEmpty(e.target.value))}
                  className={inputCls}
                />
              </Field>
              <Field label="Min ACoS (%)">
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={form.minAcos}
                  onChange={(e) => setField('minAcos', numberOrEmpty(e.target.value))}
                  className={inputCls}
                />
              </Field>
              <Field label="Min Spend (€)">
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.minSpend}
                  onChange={(e) => setField('minSpend', numberOrEmpty(e.target.value))}
                  className={inputCls}
                />
              </Field>
            </div>
          </Section>

          {/* Sektion 4: Limits */}
          <Section title="Limits">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cooldown (Min)">
                <input
                  type="number"
                  min={60}
                  value={form.cooldownMinutes}
                  onChange={(e) => setField('cooldownMinutes', Number(e.target.value) || 60)}
                  className={inputCls}
                />
              </Field>
              <Field label="Max pro Tag">
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={form.maxPerDay}
                  onChange={(e) => setField('maxPerDay', Number(e.target.value) || 1)}
                  className={inputCls}
                />
              </Field>
            </div>
          </Section>

          {/* Sektion 5: Verhalten */}
          <Section title="Verhalten">
            <CheckRow
              label="Aktiv"
              hint="Wenn deaktiviert, wird die Regel beim Cron-Lauf uebersprungen."
              checked={form.isActive}
              onChange={(v) => setField('isActive', v)}
            />
            <CheckRow
              label="Auto-Apply"
              hint="Erzeugte Events werden direkt vom Auto-Apply-Worker ausgefuehrt (nur wenn Demo-Modus aus)."
              checked={form.autoApply}
              onChange={(v) => setField('autoApply', v)}
            />
          </Section>

          {submitError && (
            <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between border-t border-[var(--border)] bg-[var(--card)] px-5 py-3">
          <div>
            {mode === 'edit' && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isPending}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Loeschen
                {eventsCount !== null && eventsCount > 0 && (
                  <span className="ml-1 rounded-full bg-red-100 px-1.5 text-[10px] font-bold tabular-nums">
                    {eventsCount}
                  </span>
                )}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-[12px] font-semibold text-[var(--foreground)] hover:bg-[var(--muted)] disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className="rounded-md bg-[var(--foreground)] px-3 py-1.5 text-[12px] font-semibold text-[var(--background)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending ? 'Speichere…' : mode === 'create' ? 'Anlegen' : 'Speichern'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

const inputCls =
  'w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2.5 py-1.5 text-[12px] text-[var(--foreground)] focus:border-[var(--foreground)] focus:outline-none'

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-5">
      <h3 className="mb-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">
        {title}
      </h3>
      {hint && <p className="mb-2 text-[11px] text-[var(--muted-foreground)]">{hint}</p>}
      <div className="space-y-2.5">{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold text-[var(--muted-foreground)]">
        {label}
      </span>
      {children}
    </label>
  )
}

function CheckRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-[var(--border)]"
      />
      <span>
        <span className="block text-[12px] font-semibold text-[var(--foreground)]">{label}</span>
        <span className="block text-[11px] text-[var(--muted-foreground)]">{hint}</span>
      </span>
    </label>
  )
}
